import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminOrganization, Plan } from '../../types/domain';
import { DataTableColumn, FilterRule } from '../../types/grid';
import { SortingState } from '@tanstack/react-table';
import { DataTable, BulkAction } from '../grid/DataTable';
import { IdCell } from '../grid/IdCell';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import {
  Trash2,
  AlertTriangle,
  Loader2,
  ExternalLink,
  Sliders,
  X,
  Building2,
  Layers,
} from 'lucide-react';

interface AdminOrganizationsGridProps {
  organizations: AdminOrganization[];
  plans?: Plan[];
  totalRows?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (newPage: number) => void;
  onPageSizeChange?: (newSize: number) => void;
  onSearchChange?: (search: string) => void;
  onSortChange?: (field: string, dir: 'asc' | 'desc' | '', allSorts?: SortingState) => void;
  sortRules?: SortingState;
  onFilterChange?: (rules: FilterRule[]) => void;
  filterRules?: FilterRule[];
  searchValue?: string;
  isLoading?: boolean;
  updatingOrgId: string | null;
  onTierUpgrade: (org: AdminOrganization, newTier: string) => Promise<void>;
  onUpdateOrg?: (
    org: AdminOrganization,
    updates: {
      plan_tier: string;
      max_monthly_submissions: number;
      max_tables: number;
      max_team_seats: number;
    }
  ) => Promise<void>;
  onDeleteOrg?: (org: AdminOrganization) => Promise<void>;
  onBulkDeleteOrgs?: (orgs: AdminOrganization[]) => Promise<void>;
  onRefresh: () => void;
  isRefreshing: boolean;
  persistenceKey?: string;
}

interface BulkDeleteBannerProps {
  targets: AdminOrganization[];
  isDeleting: boolean;
  onConfirm: (targets: AdminOrganization[]) => Promise<void>;
  onCancel: () => void;
}

const BulkDeleteBanner: React.FC<BulkDeleteBannerProps> = ({
  targets,
  isDeleting,
  onConfirm,
  onCancel,
}) => {
  const [input, setInput] = useState('');
  const [showAllBadges, setShowAllBadges] = useState(false);

  const requiredPhrase = targets.length === 1 ? targets[0].slug : 'DELETE';
  const inputClean = input.trim();
  const isConfirmed =
    targets.length === 1
      ? inputClean === targets[0].slug || inputClean.toUpperCase() === 'DELETE'
      : inputClean.toUpperCase() === 'DELETE';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isConfirmed || isDeleting) return;
    await onConfirm(targets);
  };

  const visibleBadges = showAllBadges ? targets : targets.slice(0, 8);
  const remainingCount = targets.length - 8;

  return (
    <div className="mb-3 p-4 rounded-lg bg-rose-500/10 border border-rose-500/30 text-xs space-y-3 transition-all shrink-0">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <AlertTriangle size={18} className="text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-rose-700 dark:text-rose-300 text-sm">
              Confirm Permanent Deletion of {targets.length}{' '}
              {targets.length === 1 ? 'Organization' : 'Organizations'}
            </h3>
            <p className="text-rose-600/90 dark:text-rose-400/90 text-xs mt-0.5">
              This will permanently destroy all projects, datastores, rows, and object storage (S3/local) files
              for the selected {targets.length === 1 ? 'organization' : 'organizations'}.
              Any test users belonging solely to these organizations will be pruned from the database.
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          onClick={onCancel}
          disabled={isDeleting}
          className="text-cf-textMuted hover:text-cf-textPrimary"
          title="Cancel deletion"
        >
          <X size={16} />
        </Button>
      </div>

      {/* Badges of selected organizations with high-volume clamp */}
      <div className="flex items-center gap-1.5 flex-wrap max-h-28 overflow-y-auto py-1">
        {visibleBadges.map((o) => (
          <span
            key={o.id}
            className="px-2 py-0.5 rounded bg-rose-500/20 text-rose-700 dark:text-rose-300 font-sans text-xs border border-rose-500/30 truncate max-w-[200px]"
            title={`${o.name} (${o.slug})`}
          >
            {o.name} ({o.slug})
          </span>
        ))}
        {targets.length > 8 && (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={() => setShowAllBadges((prev) => !prev)}
            className="bg-rose-500/15 hover:bg-rose-500/25 text-rose-700 dark:text-rose-300 border border-rose-500/30 font-semibold"
          >
            {showAllBadges ? 'Show fewer' : `+${remainingCount} more...`}
          </Button>
        )}
      </div>

      {/* Form Action Bar */}
      <form onSubmit={handleSubmit} className="flex items-center gap-3 flex-wrap pt-1">
        <div className="flex items-center gap-2">
          <label className="text-cf-textSecondary text-xs uppercase font-semibold">
            Type{' '}
            <strong className="text-cf-textPrimary font-sans">
              {requiredPhrase}
            </strong>{' '}
            to confirm:
          </label>
          <Input
            size="sm"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={requiredPhrase}
            disabled={isDeleting}
            className="w-44"
            autoFocus
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="submit"
            variant="danger"
            size="sm"
            disabled={isDeleting || !isConfirmed}
            isLoading={isDeleting}
            className="bg-rose-600 hover:bg-rose-700 text-white font-semibold"
          >
            {isDeleting
              ? 'Deleting...'
              : `Permanently Delete ${targets.length} ${
                  targets.length === 1 ? 'Organization' : 'Organizations'
                }`}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export const AdminOrganizationsGrid: React.FC<AdminOrganizationsGridProps> = ({
  organizations,
  plans,
  totalRows,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  onSortChange,
  sortRules,
  onFilterChange,
  filterRules,
  searchValue,
  isLoading = false,
  onTierUpgrade,
  onUpdateOrg,
  onDeleteOrg,
  onBulkDeleteOrgs,
  onRefresh,
  isRefreshing,
  persistenceKey,
}) => {
  const navigate = useNavigate();

  // Multi-selection state
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  // In-page mass deletion confirmation state (no popup)
  const [bulkDeleteTargets, setBulkDeleteTargets] = useState<AdminOrganization[] | null>(null);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Edit organization slide-over drawer state
  const [editingOrg, setEditingOrg] = useState<AdminOrganization | null>(null);
  const [editTier, setEditTier] = useState<string>('free');
  const [editMaxSubmissions, setEditMaxSubmissions] = useState<number>(500);
  const [editMaxTables, setEditMaxTables] = useState<number>(3);
  const [editMaxSeats, setEditMaxSeats] = useState<number>(2);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (editingOrg) {
      setEditTier(editingOrg.plan_tier?.toLowerCase() || 'free');
      setEditMaxSubmissions(editingOrg.max_monthly_submissions || 500);
      setEditMaxTables(editingOrg.max_tables || 3);
      setEditMaxSeats(editingOrg.max_team_seats || 2);
    }
  }, [editingOrg]);

  const handleTierSelect = (newTier: string) => {
    setEditTier(newTier);
    const matched = plans?.find((p) => p.slug.toLowerCase() === newTier.toLowerCase());
    if (matched) {
      setEditMaxSubmissions(matched.max_monthly_submissions);
      setEditMaxTables(matched.max_tables);
      setEditMaxSeats(matched.max_team_seats);
    }
  };

  const handleSaveOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOrg) return;
    setIsSaving(true);
    try {
      if (onUpdateOrg) {
        await onUpdateOrg(editingOrg, {
          plan_tier: editTier,
          max_monthly_submissions: editMaxSubmissions,
          max_tables: editMaxTables,
          max_team_seats: editMaxSeats,
        });
      } else {
        await onTierUpgrade(editingOrg, editTier);
      }
      setEditingOrg(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleExecuteBulkDelete = async (targets: AdminOrganization[]) => {
    setIsBulkDeleting(true);
    try {
      if (onBulkDeleteOrgs) {
        await onBulkDeleteOrgs(targets);
      } else if (onDeleteOrg) {
        for (const org of targets) {
          await onDeleteOrg(org);
        }
      }
      setBulkDeleteTargets(null);
      setSelectedRowIds([]);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const columns = useMemo<DataTableColumn<AdminOrganization>[]>(
    () => [
      {
        id: 'id',
        header: 'ID',
        accessorKey: 'id',
        width: 110,
        minWidth: 110,
        maxWidth: 110,
        sortable: true,
        filterable: true,
        pinned: 'left',
        isSystem: true,
        cell: ({ row }) => <IdCell id={row.id} />,
      },
      {
        id: 'name',
        header: 'Organization',
        accessorKey: 'name',
        width: 240,
        minWidth: 200,
        maxWidth: 320,
        sortable: true,
        filterable: true,
        cell: ({ row }) => (
          <Button
            type="button"
            variant="link"
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/organizations/${row.id}`);
            }}
            className="font-sans font-semibold text-cf-textPrimary hover:text-cf-orange hover:underline text-xs truncate block text-left"
            title={`Manage Organization: ${row.name}`}
          >
            <span className="truncate">{row.name}</span>
          </Button>
        ),
      },
      {
        id: 'owner_email',
        header: 'Owner Email',
        accessorKey: 'owner_email',
        width: 240,
        minWidth: 180,
        maxWidth: 300,
        dataType: 'email',
        sortable: true,
        filterable: true,
        cell: ({ row }) => {
          if (!row.owner_email) {
            return <span className="text-cf-textMuted text-xs">—</span>;
          }
          const handleClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (row.owner_id) {
              navigate(`/admin/tenants/${row.owner_id}`);
            } else {
              navigate(`/admin/tenants?search=${encodeURIComponent(row.owner_email)}`);
            }
          };
          return (
            <Button
              type="button"
              variant="link"
              size="xs"
              onClick={handleClick}
              className="font-sans text-xs text-cf-textPrimary hover:text-cf-orange truncate block text-left font-normal"
              title={`Inspect User (${row.owner_email})`}
            >
              <span className="truncate">{row.owner_email}</span>
            </Button>
          );
        },
      },
      {
        id: 'submissions_used',
        header: 'Submissions Quota',
        accessorFn: (row) => row.submissions_used,
        width: 170,
        minWidth: 140,
        maxWidth: 200,
        dataType: 'number',
        sortable: true,
        filterable: true,
        cell: ({ row }) => (
          <span className="text-xs text-cf-textPrimary tabular-nums truncate whitespace-nowrap">
            {(row.submissions_used || 0).toLocaleString()} /{' '}
            {(row.max_monthly_submissions || 0).toLocaleString()}
          </span>
        ),
      },
      {
        id: 'plan_tier',
        header: 'Plan Tier',
        accessorKey: 'plan_tier',
        width: 130,
        minWidth: 110,
        maxWidth: 150,
        sortable: true,
        filterable: true,
        cell: ({ row }) => {
          const tier = (row.plan_tier || 'free').toLowerCase();
          const badgeClass =
            tier === 'enterprise'
              ? 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/30'
              : tier === 'pro'
              ? 'bg-cf-orange/10 text-cf-orange border border-cf-orange/30'
              : 'bg-cf-bg text-cf-textSecondary border border-cf-border';
          return (
            <span
              className={`px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wider ${badgeClass}`}
            >
              {row.plan_tier || 'free'}
            </span>
          );
        },
      },
      {
        id: 'tables_count',
        header: 'Datastores',
        accessorFn: (row) => row.tables_count,
        width: 120,
        minWidth: 100,
        maxWidth: 140,
        dataType: 'number',
        sortable: true,
        filterable: true,
        cell: ({ row }) => (
          <span className="text-cf-textSecondary tabular-nums text-xs">
            {row.tables_count || 0} / {row.max_tables || 0}
          </span>
        ),
      },
      {
        id: 'seats_count',
        header: 'Seats',
        accessorFn: (row) => row.seats_count,
        width: 120,
        minWidth: 100,
        maxWidth: 140,
        dataType: 'number',
        sortable: true,
        filterable: true,
        cell: ({ row }) => (
          <span className="text-cf-textSecondary tabular-nums text-xs">
            {row.seats_count || 0} / {row.max_team_seats || 0}
          </span>
        ),
      },
      {
        id: 'created_at',
        header: 'Created At',
        accessorKey: 'created_at',
        width: 180,
        minWidth: 160,
        maxWidth: 200,
        dataType: 'date',
        truncate: false,
        sortable: true,
        filterable: true,
      },
      {
        id: 'actions',
        header: 'Actions',
        width: 110,
        minWidth: 100,
        maxWidth: 120,
        pinned: 'right',
        isSystem: true,
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={() => navigate(`/admin/organizations/${row.id}`)}
              className="text-cf-textMuted hover:text-cf-orange"
              title={`Manage Organization, Projects & Datastores: ${row.name}`}
            >
              <ExternalLink size={13} />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={() => setEditingOrg(row)}
              className="text-cf-textMuted hover:text-cf-textPrimary"
              title={`Quick Edit Quotas: ${row.name}`}
            >
              <Sliders size={13} />
            </Button>
            {(onDeleteOrg || onBulkDeleteOrgs) && (
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={() => {
                  setBulkDeleteTargets([row]);
                }}
                className="text-cf-textMuted hover:text-rose-400"
                title={`Delete Organization: ${row.name}`}
              >
                <Trash2 size={13} />
              </Button>
            )}
          </div>
        ),
      },
    ],
    [navigate, onDeleteOrg, onBulkDeleteOrgs]
  );

  const bulkActions = useMemo<BulkAction<AdminOrganization>[] | undefined>(() => {
    if (!onDeleteOrg && !onBulkDeleteOrgs) return undefined;
    return [
      {
        label: `Delete Selected (${selectedRowIds.length})`,
        icon: <Trash2 size={13} />,
        variant: 'danger',
        onClick: (selectedRows: AdminOrganization[]) => {
          setBulkDeleteTargets(selectedRows);
        },
      },
    ];
  }, [onDeleteOrg, onBulkDeleteOrgs, selectedRowIds.length]);

  return (
    <div className="flex-1 min-h-0 min-w-0 w-full max-w-full overflow-hidden flex flex-col">
      {/* In-Page Mass Deletion Confirmation Banner (Isolated subcomponent for 60fps zero-lag typing) */}
      {bulkDeleteTargets && bulkDeleteTargets.length > 0 && (
        <BulkDeleteBanner
          targets={bulkDeleteTargets}
          isDeleting={isBulkDeleting}
          onConfirm={handleExecuteBulkDelete}
          onCancel={() => setBulkDeleteTargets(null)}
        />
      )}

      {/* Main Server-Paginated Table with Checkbox Row Selection */}
      <DataTable<AdminOrganization>
        persistenceKey={persistenceKey || 'admin_organizations'}
        columns={columns}
        data={organizations}
        serverSide={true}
        totalRows={totalRows}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onServerSearchChange={onSearchChange}
        onServerSortChange={onSortChange}
        initialSorting={sortRules}
        onServerFilterChange={onFilterChange}
        initialFilterRules={filterRules}
        searchValue={searchValue}
        isLoading={isLoading}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
        enableSelection={true}
        selectedRowIds={selectedRowIds}
        onSelectionChange={setSelectedRowIds}
        bulkActions={bulkActions}
        emptyTitle="No organizations found"
        emptySubtitle="Try adjusting your search or filter rules."
      />

      {/* Quick Edit Quotas Slide-Over Drawer (Spacious Right Drawer, No Popup) */}
      {editingOrg && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-xs transition-opacity"
            onClick={() => setEditingOrg(null)}
          />
          <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
            <div className="w-screen max-w-md bg-cf-surface border-l border-cf-border shadow-2xl flex flex-col">
              {/* Drawer Header */}
              <div className="px-6 py-4 border-b border-cf-border flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded bg-cf-orange/10 border border-cf-orange/30 flex items-center justify-center text-cf-orange">
                    <Building2 size={16} />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-cf-textPrimary">Adjust Organization Quotas</h2>
                    <p className="text-xs text-cf-textSecondary font-sans">{editingOrg.name} ({editingOrg.slug})</p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setEditingOrg(null)}
                  className="text-cf-textMuted hover:text-cf-textPrimary"
                >
                  <X size={16} />
                </Button>
              </div>

              {/* Drawer Body */}
              <form onSubmit={handleSaveOrg} className="flex-1 overflow-y-auto p-6 space-y-5 text-xs">
                <div className="p-3 bg-cf-bg border border-cf-border rounded-lg space-y-1.5">
                  <div className="flex justify-between">
                    <span className="text-cf-textMuted text-xs uppercase font-semibold">Organization ID</span>
                    <code className="font-sans text-xs text-cf-textPrimary">{editingOrg.id}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-cf-textMuted text-xs uppercase font-semibold">Owner Email</span>
                    <span className="text-cf-textPrimary font-sans">{editingOrg.owner_email || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-cf-textMuted text-xs uppercase font-semibold">Current Datastores</span>
                    <span className="text-cf-textPrimary font-bold">{editingOrg.tables_count || 0}</span>
                  </div>
                </div>

                <div>
                  <Select
                    label="Select Plan Tier"
                    value={editTier}
                    onChange={(e) => handleTierSelect(e.target.value)}
                    options={plans && plans.length > 0 ? (
                      plans.map((p) => ({
                        value: p.slug,
                        label: `${p.name} (${(p.max_monthly_submissions || 0).toLocaleString()} subs / ${p.max_tables} tables / ${p.max_team_seats} seats)`,
                      }))
                    ) : [
                      { value: 'free', label: 'Free Tier' },
                      { value: 'pro', label: 'Pro Tier' },
                      { value: 'enterprise', label: 'Enterprise Tier' },
                    ]}
                  />
                </div>

                <div>
                  <Input
                    label="Max Monthly Submissions"
                    type="number"
                    min="0"
                    step="500"
                    value={editMaxSubmissions}
                    onChange={(e) => setEditMaxSubmissions(Number(e.target.value))}
                    className="font-sans tabular-nums"
                  />
                </div>

                <div>
                  <Input
                    label="Max Datastores / Tables"
                    type="number"
                    min="1"
                    value={editMaxTables}
                    onChange={(e) => setEditMaxTables(Number(e.target.value))}
                    className="font-sans tabular-nums"
                  />
                </div>

                <div>
                  <Input
                    label="Max Team Seats"
                    type="number"
                    min="1"
                    value={editMaxSeats}
                    onChange={(e) => setEditMaxSeats(Number(e.target.value))}
                    className="font-sans tabular-nums"
                  />
                </div>

                <div className="pt-4 border-t border-cf-border flex justify-end gap-2.5">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditingOrg(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isSaving}
                    loading={isSaving}
                    icon={isSaving ? <Loader2 size={13} className="animate-spin" /> : undefined}
                  >
                    Save Quotas
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
